import { useEffect, useState } from 'react';

import { TabNavGroupProps, TabNavProps } from './types';

/**
 * A component for tab navigation that:
 * 1. Highlights when its corresponding section is in view
 * 2. Smoothly scrolls to its target section when clicked
 * 3. Updates the active tab state based on scroll position
 */
const TabNavButton = ({
  title,
  targetElementId,
  activeTab,
  setActiveTab,
}: TabNavProps) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveTab(targetElementId);
        }
      },
      {
        rootMargin: '-40px 0px -50% 0px',
        threshold: 0.1,
      }
    );

    const element = document.getElementById(targetElementId);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [targetElementId, setActiveTab]);

  return (
    <button
      type="button"
      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
        activeTab === targetElementId
          ? 'bg-brand-green-25 text-brand-green-800'
          : 'text-tertiary hover:bg-secondary'
      }`}
      onClick={() => {
        const element = document.getElementById(targetElementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }}
    >
      {title}
    </button>
  );
};

export const TabNavGroup = ({ defaultTab, tabs }: TabNavGroupProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  return (
    <div className="flex flex-row items-center gap-2 text-sm">
      {tabs.map((tab) => (
        <TabNavButton
          key={tab.targetElementId}
          title={tab.title}
          targetElementId={tab.targetElementId}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      ))}
    </div>
  );
};
